import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Flame, TrendingUp, TrendingDown, Minus, Eye, Heart, Send, Clock,
    ArrowUpDown, ArrowUp, ArrowDown, Sparkles, CalendarClock,
    Target, Phone, Mail, Building2, MapPin, Wallet, BedDouble,
    ChevronRight, ChevronDown,
} from 'lucide-react';
import { fetchTopEngagedBuyers } from '../api/analyticsApi';
import BuyerActionsModal from '../../../components/properties/BuyerActionsModal';

/**
 * Top Engaged Buyers (Buyer Intent Score)
 *
 * The redesigned 0-100 intent score served straight off the nightly
 * BuyerIntentScore. Shows the headline score + category band, week-over-week
 * trend (direction), finance + timeline labels, an activity summary, and a
 * "Most Likely To Buy (Next 90 Days)" revenue-prediction strip at the top.
 */

// Intent category bands → colour + copy (mirrors the backend CATEGORY_BANDS).
const CATEGORY_META = {
    priority: { label: 'Priority', text: 'text-red-700', bg: 'bg-red-50', ring: 'border-red-200', dot: 'bg-red-500', blurb: 'Immediate follow-up' },
    hot: { label: 'Hot', text: 'text-orange-700', bg: 'bg-orange-50', ring: 'border-orange-200', dot: 'bg-orange-500', blurb: 'Agent call' },
    active: { label: 'Active', text: 'text-amber-700', bg: 'bg-amber-50', ring: 'border-amber-200', dot: 'bg-amber-500', blurb: 'Agent visibility' },
    warm: { label: 'Warm', text: 'text-sky-700', bg: 'bg-sky-50', ring: 'border-sky-200', dot: 'bg-sky-500', blurb: 'Email nurture' },
    cold: { label: 'Cold', text: 'text-gray-600', bg: 'bg-gray-50', ring: 'border-gray-200', dot: 'bg-gray-400', blurb: 'Marketing nurture only' },
};

const catMeta = (c) => CATEGORY_META[c] || CATEGORY_META.cold;

const FINANCE_COLORS = {
    'Cash': '#16a34a',
    'Pre-approved': '#0d9488',
    'Broker': '#7c3aed',
    'Not ready': '#9ca3af',
};

// Human-readable names for the 10 scoring categories — mirrors the backend
// intent_scoring_v2 tiers (Profile 1-4 = declared, Activity 5-10 = activity).
const TIER_META = {
    1: 'Finance status',
    2: 'Investor traits',
    3: 'Timeframe',
    4: 'Completed profile',
    5: 'Offers',
    6: 'Inspections',
    7: 'High-intent enquiries',
    8: 'General enquiries',
    9: 'Calculator use',
    10: 'Views & saves',
};

// Mirrors intent_scoring_v2.DECLARED_CAP / ACTIVITY_CAP — the most each pool can
// contribute. Declared = "who they are", Activity = "what they did"; they're
// capped separately and summed, so 100 needs a strong profile AND real activity.
// Kept in sync with the backend; the composite the API returns is authoritative,
// these only explain the maths in the breakdown.
const DECLARED_CAP = 40;
const ACTIVITY_CAP = 60;

// Expandable per-buyer score audit. The backend tags every signal as either
// 'declared' (who the buyer is — profile/finance/investor/timeframe) or
// 'activity' (what they actually did — views/saves/enquiries/calculators/chat),
// and caps the declared pool at DECLARED_CAP. We surface that split so an agent
// can see at a glance how much of the score is "real engagement" vs "profile",
// and why a strong profile alone tops out in the Active band.
const ScoreBreakdown = ({ signals, score }) => {
    if (!signals || signals.length === 0) {
        return (
            <div className="px-4 py-3 text-xs text-gray-400 italic">
                No signal breakdown available yet — scores are recomputed nightly.
            </div>
        );
    }
    // Older rows (scored before the declared/activity split shipped) have no
    // `kind` — fall back to treating everything as one pool so they still render.
    const tagged = signals.some((s) => s.kind === 'declared' || s.kind === 'activity');
    const declaredRaw = signals.filter((s) => s.kind === 'declared').reduce((n, s) => n + (s.points || 0), 0);
    const activityRaw = signals.filter((s) => s.kind === 'activity').reduce((n, s) => n + (s.points || 0), 0);
    const declaredScore = Math.min(declaredRaw, DECLARED_CAP);
    const activityScore = Math.min(activityRaw, ACTIVITY_CAP);
    const declaredCapped = declaredRaw > declaredScore;
    const activityCapped = activityRaw > activityScore;

    const byTier = {};
    signals.forEach((s) => {
        (byTier[s.tier] = byTier[s.tier] || []).push(s);
    });
    const tiers = Object.keys(byTier).map(Number).sort((a, b) => a - b);
    return (
        <div className="px-4 py-3 bg-gray-50/70 border-t border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
                Why this score
            </div>

            {/* Declared vs activity summary — the headline of the redesign */}
            {tagged && (
                <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-violet-50 text-violet-700">
                        <span className="font-semibold">Profile</span>
                        <span className="text-violet-400">(who they are)</span>
                        <span className="font-bold tabular-nums">
                            +{declaredScore}
                            {declaredCapped && <span className="font-normal text-violet-400"> of {declaredRaw}, capped at {DECLARED_CAP}</span>}
                        </span>
                    </span>
                    <span className="text-gray-300">+</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-teal-50 text-teal-700">
                        <span className="font-semibold">Activity</span>
                        <span className="text-teal-400">(what they did)</span>
                        <span className="font-bold tabular-nums">
                            +{activityScore}
                            {activityCapped && <span className="font-normal text-teal-400"> of {activityRaw}, capped at {ACTIVITY_CAP}</span>}
                        </span>
                    </span>
                    <span className="text-gray-300">=</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-900 text-white">
                        <span className="font-semibold">Intent score</span>
                        <span className="font-bold tabular-nums">{score}</span>
                    </span>
                    {declaredCapped && activityRaw === 0 && (
                        <span className="text-amber-600">Profile-only — needs site activity to reach Hot/Priority.</span>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                {tiers.map((t) => {
                    const items = byTier[t];
                    const total = items.reduce((sum, i) => sum + (i.points || 0), 0);
                    return (
                        <div key={t}>
                            <div className="flex items-center justify-between mb-1 pb-1 border-b border-gray-200">
                                <span className="text-[11px] font-bold text-gray-700">{TIER_META[t] || `Tier ${t}`}</span>
                                <span className="text-[11px] font-bold text-teal-700 tabular-nums">+{total}</span>
                            </div>
                            <ul className="space-y-0.5">
                                {items.map((i, idx) => (
                                    <li key={`${t}-${idx}`} className="flex items-center justify-between text-xs text-gray-600">
                                        <span className="truncate pr-2 flex items-center gap-1.5">
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${i.kind === 'activity' ? 'bg-teal-500' : 'bg-violet-400'}`} />
                                            {i.label}
                                        </span>
                                        <span className="font-medium text-gray-400 tabular-nums">+{i.points}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${Math.max(0, diffMins)}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

const ScoreBadge = ({ score, category }) => {
    const m = catMeta(category);
    return (
        <div className="flex items-center gap-2">
            <div className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg ${m.bg} ${m.text} border ${m.ring}`}>
                <span className="text-lg font-bold leading-none">{score}</span>
                <span className="text-[8px] uppercase tracking-wide opacity-70">/100</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${m.bg} ${m.text}`}>
                {m.label}
            </span>
        </div>
    );
};

const TrendCell = ({ trend, prevWeek }) => {
    if (prevWeek === null || prevWeek === undefined) {
        return <span className="text-[11px] text-gray-400 italic">new</span>;
    }
    if (trend > 0) {
        return (
            <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
                <TrendingUp className="w-4 h-4" /> +{trend}
            </span>
        );
    }
    if (trend < 0) {
        return (
            <span className="inline-flex items-center gap-1 text-red-500 font-semibold text-sm">
                <TrendingDown className="w-4 h-4" /> {trend}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
            <Minus className="w-4 h-4" /> 0
        </span>
    );
};

const Chip = ({ color, children }) => (
    <span
        className="px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
        style={{ background: `${color}1a`, color }}
    >
        {children}
    </span>
);

// $1,725,000 -> "$1.73M"; $620,000 -> "$620K"
const fmtBudget = (n) => {
    if (n == null) return null;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return `$${n.toLocaleString()}`;
};

const budgetLabel = (lf) => {
    if (!lf) return null;
    const lo = fmtBudget(lf.price_min);
    const hi = fmtBudget(lf.price_max);
    if (!lo && !hi) return null;
    return `${lo ?? '—'}–${hi ?? '—'}`;
};

// "What to call them about": property types, budget band and target suburbs from
// the buyer's declared Buying Preferences. Rendered both in the list row and the
// compact "Most Likely To Buy" cards.
const LookingFor = ({ lf, compact = false }) => {
    if (!lf) {
        return compact ? null : <span className="text-gray-300 text-xs">—</span>;
    }
    const budget = budgetLabel(lf);
    const types = lf.property_types || [];
    const suburbs = lf.suburbs || [];
    const bedrooms = lf.bedrooms;
    if (!budget && !bedrooms && types.length === 0 && suburbs.length === 0) {
        return compact ? null : <span className="text-gray-300 text-xs">—</span>;
    }
    return (
        <div className="flex flex-wrap items-center gap-1">
            {budget && <Chip color="#0e9f6e"><Wallet className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{budget}</Chip>}
            {bedrooms && <Chip color="#7c3aed"><BedDouble className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{bedrooms}</Chip>}
            {types.map((t) => (
                <Chip key={t} color="#2563eb"><Building2 className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{t}</Chip>
            ))}
            {!compact && suburbs.map((s) => (
                <Chip key={s} color="#6b7280"><MapPin className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{s}</Chip>
            ))}
        </div>
    );
};

const TopEngagedBuyers = ({ days = 30 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [sortField, setSortField] = useState('intent_score');
    const [sortDirection, setSortDirection] = useState('desc');
    // Buyer whose quick-actions (notes + "call again" reminder) popup is open.
    // Clicking a buyer logs a note / sets a reminder in place — it deliberately
    // does NOT navigate to the full contact profile.
    const [actionBuyer, setActionBuyer] = useState(null);
    // Buyer whose "Why this score" breakdown row is expanded (one at a time).
    const [expandedId, setExpandedId] = useState(null);

    const toggleExpand = useCallback((id) => {
        setExpandedId((cur) => (cur === id ? null : id));
    }, []);

    // Map a Top-Engaged row to the shape BuyerActionsModal expects.
    const openActions = useCallback((b) => {
        setActionBuyer({
            id: b.buyer_id,
            name: b.buyer_name,
            email: b.buyer_email,
            phone: b.buyer_phone || '',
        });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await fetchTopEngagedBuyers(200, Math.min(365, Math.max(1, days)));
            setData(res);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const buyers = data?.buyers || [];

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const sorted = useMemo(() => {
        const arr = [...buyers];
        arr.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (sortField === 'buyer_name') {
                av = (av || '').toLowerCase();
                bv = (bv || '').toLowerCase();
                return sortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            if (sortField === 'last_activity') {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
            }
            av = av || 0;
            bv = bv || 0;
            return sortDirection === 'asc' ? av - bv : bv - av;
        });
        return arr;
    }, [buyers, sortField, sortDirection]);

    const likely = useMemo(() => buyers.filter((b) => b.likely_transaction), [buyers]);

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-gray-300" />;
        return sortDirection === 'asc'
            ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-teal-600" />
            : <ArrowDown className="w-3.5 h-3.5 ml-1 text-teal-600" />;
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-500" />
                        Top Engaged Buyers <span className="text-gray-400 font-semibold">(Buyer Intent Score)</span>
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        0–100 intent = profile (max {DECLARED_CAP}) + activity (max {ACTIVITY_CAP}), each capped separately — so 100 needs both a strong profile and real engagement, and a profile alone only reaches nurture range. Expand a row (chevron) to see the split; click a buyer to log a note or set a call-back reminder.
                    </p>
                </div>
            </div>

            {/* Category legend */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 mb-5">
                {['priority', 'hot', 'active', 'warm', 'cold'].map((c) => {
                    const m = catMeta(c);
                    return (
                        <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                            <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                            <span className="font-semibold text-gray-700">{m.label}</span>
                            <span className="text-gray-400">· {m.blurb}</span>
                        </span>
                    );
                })}
            </div>

            {/* Revenue prediction strip */}
            {likely.length > 0 && (
                <div className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4 text-emerald-600" />
                        <h3 className="text-sm font-bold text-emerald-800">
                            Most Likely To Buy — Next 90 Days
                        </h3>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                            {likely.length}
                        </span>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
                        {likely.map((b) => (
                            <button
                                type="button"
                                key={`likely-${b.buyer_id}`}
                                onClick={() => openActions(b)}
                                className="flex-shrink-0 text-left bg-white rounded-lg border border-emerald-100 px-3 py-2 hover:shadow-md transition-shadow min-w-[180px]"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-semibold text-gray-900 truncate">{b.buyer_name}</span>
                                    <span className="text-base font-bold text-emerald-700">{b.intent_score}</span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                    {b.finance_label && (
                                        <Chip color={FINANCE_COLORS[b.finance_label] || '#0d9488'}>{b.finance_label}</Chip>
                                    )}
                                    {b.timeline_label && <Chip color="#ea580c">{b.timeline_label}</Chip>}
                                </div>
                                <div className="mt-1.5">
                                    <LookingFor lf={b.looking_for} compact />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* States */}
            {loading ? (
                <div className="py-10 text-center text-gray-400 text-sm">Loading intent scores…</div>
            ) : error ? (
                <div className="py-10 text-center text-gray-400 text-sm">
                    Couldn’t load intent scores.{' '}
                    <button onClick={load} className="text-teal-600 font-medium hover:underline">Retry</button>
                </div>
            ) : sorted.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">
                    No intent scores yet — they’re computed nightly from buyer activity and profiles.
                </div>
            ) : (
                <div className="overflow-x-auto overflow-y-auto max-h-[640px] -mx-2">
                    <table className="min-w-full">
                        <thead className="sticky top-0 bg-white z-10">
                            <tr className="border-b border-gray-200">
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('buyer_name')}>
                                    <div className="flex items-center">Buyer <SortIcon field="buyer_name" /></div>
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('intent_score')}>
                                    <div className="flex items-center">Intent Score <SortIcon field="intent_score" /></div>
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('score_trend')}>
                                    <div className="flex items-center">Trend <SortIcon field="score_trend" /></div>
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Finance</th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Timeline</th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Looking For</th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Activity</th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('last_activity')}>
                                    <div className="flex items-center">Last Seen <SortIcon field="last_activity" /></div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map((b) => {
                              const isExpanded = expandedId === b.buyer_id;
                              return (
                                <React.Fragment key={b.buyer_id}>
                                <tr
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => openActions(b)}
                                >
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${catMeta(b.intent_category).bg} ${catMeta(b.intent_category).text}`}>
                                                {b.buyer_name?.charAt(0).toUpperCase() || 'B'}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                                    {b.buyer_name}
                                                    {b.likely_transaction && (
                                                        <Sparkles className="w-3.5 h-3.5 text-emerald-500" title="Likely to buy in 90 days" />
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">{b.buyer_email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <ScoreBadge score={b.intent_score} category={b.intent_category} />
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); toggleExpand(b.buyer_id); }}
                                                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                                title={isExpanded ? 'Hide score breakdown' : 'Why this score?'}
                                                aria-label={isExpanded ? 'Hide score breakdown' : 'Why this score?'}
                                                aria-expanded={isExpanded}
                                            >
                                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <TrendCell trend={b.score_trend} prevWeek={b.score_prev_week} />
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        {b.finance_label
                                            ? <Chip color={FINANCE_COLORS[b.finance_label] || '#0d9488'}>{b.finance_label}</Chip>
                                            : <span className="text-gray-300 text-xs" title="Finance status not provided — the buyer hasn't answered the finance question (not the same as 'Not ready')">—</span>}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        {b.timeline_label
                                            ? <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700"><CalendarClock className="w-3.5 h-3.5 text-gray-400" />{b.timeline_label}</span>
                                            : <span className="text-gray-300 text-xs">—</span>}
                                    </td>
                                    <td className="px-3 py-3 max-w-[260px]">
                                        <LookingFor lf={b.looking_for} />
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-2.5 text-xs text-gray-400">
                                            <span className="flex items-center gap-0.5" title="Property views"><Eye className="w-3.5 h-3.5" />{b.property_views || 0}</span>
                                            <span className="flex items-center gap-0.5" title="Saved"><Heart className="w-3.5 h-3.5" />{b.properties_saved || 0}</span>
                                            <span className="flex items-center gap-0.5" title="Enquiries"><Send className="w-3.5 h-3.5" />{b.enquiries_sent || 0}</span>
                                            {(b.calls_clicked || 0) > 0 && <span className="flex items-center gap-0.5" title="Call taps"><Phone className="w-3.5 h-3.5" />{b.calls_clicked}</span>}
                                            {(b.emails_clicked || 0) > 0 && <span className="flex items-center gap-0.5" title="Email taps"><Mail className="w-3.5 h-3.5" />{b.emails_clicked}</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                            <Clock className="w-3.5 h-3.5 text-gray-400" />{formatDate(b.last_activity)}
                                        </span>
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="bg-gray-50/70">
                                        <td colSpan={8} className="p-0">
                                            <ScoreBreakdown signals={b.signals} score={b.intent_score} />
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Notes + call-back reminder popup — opens in place, no navigation.
                Deliberately no `onViewProfile`/`propertyId` so it stays a quick
                "log a note / set a reminder" action, not a jump to the profile. */}
            {actionBuyer && (
                <BuyerActionsModal
                    buyer={actionBuyer}
                    onClose={() => setActionBuyer(null)}
                />
            )}
        </div>
    );
};

export default TopEngagedBuyers;
